import { useState, useRef, useEffect, useCallback, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';
import styles from './TOTPInput.module.css';

interface TOTPInputProps {
  /** Callback when all 6 digits are entered */
  onComplete: (code: string) => void;
  /** Disable the input */
  disabled?: boolean;
  /** Show error state with optional message */
  error?: string | boolean;
  /** Show success state */
  success?: boolean;
  /** Auto-focus the first input on mount */
  autoFocus?: boolean;
  /** Clear the input (controlled by parent) */
  clearTrigger?: number;
}

const DIGIT_COUNT = 6;

export function TOTPInput({
  onComplete,
  disabled = false,
  error,
  success = false,
  autoFocus = true,
  clearTrigger,
}: TOTPInputProps) {
  const [digits, setDigits] = useState<string[]>(Array(DIGIT_COUNT).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Clear inputs when clearTrigger changes
  useEffect(() => {
    if (clearTrigger !== undefined) {
      setDigits(Array(DIGIT_COUNT).fill(''));
      inputRefs.current[0]?.focus();
    }
  }, [clearTrigger]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && !disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [autoFocus, disabled]);

  // Check if all digits are filled and trigger onComplete
  const checkComplete = useCallback((newDigits: string[]) => {
    const code = newDigits.join('');
    if (code.length === DIGIT_COUNT && newDigits.every(d => d !== '')) {
      onComplete(code);
    }
  }, [onComplete]);

  const handleChange = (index: number, event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Only allow single digit
    const digit = value.replace(/\D/g, '').slice(-1);

    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);

    // Move to next input if digit entered
    if (digit && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    checkComplete(newDigits);
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    const key = event.key;

    // Handle backspace
    if (key === 'Backspace') {
      if (digits[index]) {
        // Clear current digit
        const newDigits = [...digits];
        newDigits[index] = '';
        setDigits(newDigits);
      } else if (index > 0) {
        // Move to previous input and clear it
        inputRefs.current[index - 1]?.focus();
        const newDigits = [...digits];
        newDigits[index - 1] = '';
        setDigits(newDigits);
      }
      event.preventDefault();
      return;
    }

    // Handle left arrow
    if (key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
      event.preventDefault();
      return;
    }

    // Handle right arrow
    if (key === 'ArrowRight' && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
      event.preventDefault();
      return;
    }

    // Handle delete
    if (key === 'Delete') {
      const newDigits = [...digits];
      newDigits[index] = '';
      setDigits(newDigits);
      event.preventDefault();
      return;
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');
    const pastedDigits = pastedData.replace(/\D/g, '').slice(0, DIGIT_COUNT).split('');

    if (pastedDigits.length > 0) {
      const newDigits = [...digits];
      pastedDigits.forEach((digit, i) => {
        if (i < DIGIT_COUNT) {
          newDigits[i] = digit;
        }
      });
      setDigits(newDigits);

      // Focus the next empty input or the last one
      const nextEmptyIndex = newDigits.findIndex(d => d === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[DIGIT_COUNT - 1]?.focus();
      }

      checkComplete(newDigits);
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Select the content when focused
    event.target.select();
  };

  const containerClassName = [
    styles.container,
    error ? styles.error : '',
    success ? styles.success : '',
    disabled ? styles.disabled : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={containerClassName}>
      <div className={styles.inputGroup}>
        {digits.map((digit, index) => (
          <div key={index} style={{ display: 'contents' }}>
            <input
              ref={(el) => { inputRefs.current[index] = el; }}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={1}
              value={digit}
              onChange={(e) => handleChange(index, e)}
              onKeyDown={(e) => handleKeyDown(index, e)}
              onPaste={handlePaste}
              onFocus={handleFocus}
              disabled={disabled}
              autoComplete="one-time-code"
              aria-label={`Digit ${index + 1} of ${DIGIT_COUNT}`}
              className={`${styles.digitInput} ${digit ? styles.filled : ''}`}
              placeholder="-"
            />
            {/* Add separator after 3rd digit */}
            {index === 2 && <div className={styles.separator} />}
          </div>
        ))}
      </div>

      {typeof error === 'string' && error && (
        <div className={styles.errorMessage}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v4M12 16h.01" />
          </svg>
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export default TOTPInput;
